import React from "react";
import { Controller } from "react-hook-form";
// @ts-ignore
import ReactSelect, { components } from "react-select";
// @ts-ignore
import AsyncReactSelect from "react-select/async";
import { get, chain, isString, isUndefined } from "lodash";
import ErrorTextHelper from "./ErrorTextHelper";
import FieldIcon from "./FieldIcon";

const _ = { get, chain, isString, isUndefined };

interface Props {
  name: string;
  options?: Array<OptionsProp>;
  control?: any;
  menuIsOpen?: boolean;
  externalLabel?: externalLabel;
  placeholder?: string;
  rules?: object;
  value?: any;
  search?: boolean;
  isSearchable?: boolean;
  icon?: React.ReactNode;
  loadOptions?: (inputValue: string) => any;
  onStartSearching?: (inputValue: string) => any;
  [key: string]: any;
}

export interface OptionsProp {
  label: string | React.ReactNode;
  value: string;
}

interface externalLabel {
  [key: string]: any;
}

const SelectNew = ({
  options,
  name,
  control,
  value,
  search,
  isSearchable,
  icon,
  loadOptions,
  onStartSearching,
  handleCustomOnChange,
  isClearable = false,
  className,
  ...props
}: Props) => {
  const canSearch = Boolean(search ?? isSearchable);
  const asyncLoader = loadOptions || onStartSearching;
  const isAsync = typeof asyncLoader === "function";
  const SelectComponent = isAsync ? AsyncReactSelect : ReactSelect;

  let inputRefClass = "";
  const isRequired = _.get(props, "required", "");
  if (isRequired === true) {
    inputRefClass = "required";
  }
  const externalLabel = _.get(props, "externalLabel.label", "") || "";
  let externalLabelClass = _.get(props, "externalLabel.className", "") || "";
  const externalLabelClasses = _.chain([inputRefClass, externalLabelClass])
    .uniq()
    .join(" ")
    .split(" ")
    .uniq()
    .join(" ")
    .trim()
    .value();

  const lookupErrorMessage =
    _.get(props.validationObj, `${name}.message`, null) ||
    _.get(props.validationObj, `${String(name).split("[").join(".").split("]").join("")}.message`, null);
  const noOptionsMessage = _.get(props, "noOptionsMessage", "") || "No Options";

  const Control = (controlProps: any) => (
    <components.Control {...controlProps}>
      <FieldIcon>{icon}</FieldIcon>
      {controlProps.children}
    </components.Control>
  );

  const renderSelect = (field?: any, hasError?: boolean) => {
    const selectClassName = `${hasError ? "async-error custom-select-box" : "custom-select-box"
      } ${className || ""} ${icon ? "has-icon" : ""}`.trim();

    const sharedProps = {
      name,
      className: selectClassName,
      classNamePrefix: "eh-custom-dropdown-inner",
      components: { Control, IndicatorSeparator: () => null },
      isSearchable: canSearch,
      placeholder: _.get(props, "placeholder", "Please select"),
      noOptionsMessage: () => noOptionsMessage,
      isClearable,
      menuIsOpen: _.get(props, "menuIsOpen"),
      isDisabled: props.isDisabled || props.someDisabledFlag,
      ...(typeof props.formatOptionLabel === "function"
        ? { formatOptionLabel: props.formatOptionLabel }
        : {}),
      ...(isAsync
        ? {
          cacheOptions: true,
          defaultOptions:
            props.defaultOptions !== undefined ? props.defaultOptions : true,
          loadOptions: asyncLoader,
        }
        : { options }),
    };

    return (
      <SelectComponent
        {...sharedProps}
        {...(field || {})}
        value={field ? field.value : value}
        onChange={(val: any) => {
          if (field) {
            field.onChange(val);
          }
          if (typeof props.onChange === "function") {
            props.onChange(val);
          }
          if (typeof handleCustomOnChange === "function") {
            handleCustomOnChange(val);
          }
        }}
      />
    );
  };

  return (
    <>
      {externalLabel && (
        <label htmlFor={name} className={externalLabelClasses}>
          {externalLabel}
        </label>
      )}
      <div className="MuiFormControl-root MuiFormControl-fullWidth">
        {_.isUndefined(control) ? (
          <>
            {renderSelect(undefined, !!lookupErrorMessage)}
            {lookupErrorMessage && <ErrorTextHelper text={lookupErrorMessage} />}
          </>
        ) : (
          <Controller
            name={name}
            rules={props.rules || {}}
            control={control}
            render={({ field, fieldState }) => {
              const message =
                (fieldState && fieldState.error && fieldState.error.message) ||
                lookupErrorMessage;
              return (
                <>
                  {renderSelect(field, !!message)}
                  {message ? <ErrorTextHelper text={message} /> : null}
                </>
              );
            }}
          />
        )}
      </div>
    </>
  );
};

export default SelectNew;
